import { Dictionary } from "lodash";
import { DesignationDistance } from "react-migration/domains/constraints/designation/hooks/useNearestDesignationQuery";
import { isDefined } from "react-migration/lib/util/isDefined";
import { useKeyIndicatorTags, KeyIndicatorColor } from "react-migration/lib/map/useKeyIndicatorTags";
import { isBeyondMaxDistance } from "./AmenityDistanceInsight";
import * as constants from "./constants";

function getKeyIndicatorColor(
  hasElementary: boolean,
  hasMiddle: boolean,
  hasHigh: boolean,
  hasPrivateElementary: boolean,
  hasPrivateSecondary: boolean,
  hasPrivateCombined: boolean
) {
  const publicLevelsCount = [hasElementary, hasMiddle, hasHigh].filter(Boolean).length;

  if (publicLevelsCount === 3) {
    return KeyIndicatorColor.GREEN;
  }

  const hasElementaryLevel = hasElementary || hasPrivateElementary || hasPrivateCombined;
  const hasMiddleLevel = hasMiddle || hasPrivateCombined;
  const hasHighLevel = hasHigh || hasPrivateSecondary || hasPrivateCombined;

  const totalLevelsCount = [hasElementaryLevel, hasMiddleLevel, hasHighLevel].filter(Boolean).length;

  if (totalLevelsCount >= 2) {
    return KeyIndicatorColor.ORANGE;
  }

  return KeyIndicatorColor.RED;
}

export function useNearestSchoolsKeyIndicatorUS(
  distanceMap: Dictionary<DesignationDistance | undefined> | undefined,
  maxDistanceMeters: number
) {
  const keyIndicator = useKeyIndicatorTags(() => {
    const distances = Object.values(distanceMap ?? {}).filter(isDefined);

    const withinMaxDistance = distances?.filter(
      (distance) => !isBeyondMaxDistance(distance.distanceMeters, maxDistanceMeters)
    );

    const hasElementary = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_ELEMENTARY_CATEGORY
    );
    const hasMiddle = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_MIDDLE_CATEGORY
    );
    const hasHigh = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_HIGH_CATEGORY
    );
    const hasCombination = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_COMBINATION_CATEGORY
    );

    const hasPrivateElementary = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_PRIVATE_ELEMENTARY_CATEGORY
    );
    const hasPrivateSecondary = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_PRIVATE_SECONDARY_CATEGORY
    );
    const hasPrivateCombined = withinMaxDistance?.some(
      (distance) => distance.designation.sub_category_id === constants.US_SCHOOLS_PRIVATE_COMBINED_CATEGORY
    );

    const effectiveHasElementary = hasElementary || hasCombination;
    const effectiveHasMiddle = hasMiddle || hasCombination;

    return [
      {
        color: getKeyIndicatorColor(
          effectiveHasElementary,
          effectiveHasMiddle,
          hasHigh,
          hasPrivateElementary,
          hasPrivateSecondary,
          hasPrivateCombined
        ),
        tag: withinMaxDistance?.length ? withinMaxDistance.length.toString() : "None nearby",
      },
    ];
  }, [distanceMap, maxDistanceMeters]);

  return keyIndicator;
}
